/**
 * Hook for handling document rename when the autoname field is edited.
 *
 * Manages rename dialog state, executes the rename API call,
 * saves other field changes after rename, invalidates caches,
 * and navigates to the new URL.
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getERPClient } from "@/lib/erp/client";
import { buildDetailRoute } from "@/lib/config/docRoutes";
import { detectRename } from "./detectRename";

interface RenameDialogState {
  oldName: string;
  newName: string;
  autoNameField: string;
}

import type { DocTypeSchema } from "@/lib/erp/meta";

interface UseRenameHandlerParams {
  doctype: string;
  docName: string;
  isNew: boolean;
  metaSchema: DocTypeSchema | undefined;
}

/** Compare formData vs originalData, returning only changed fields (excluding the autoname field). */
function computeNonRenameChanges(
  formData: Record<string, any>,
  originalData: Record<string, any>,
  excludeField: string
): Record<string, any> {
  const changes: Record<string, any> = {};
  for (const key of Object.keys(formData)) {
    if (key === excludeField) continue;
    if (JSON.stringify(formData[key]) !== JSON.stringify(originalData[key])) {
      changes[key] = formData[key];
    }
  }
  return changes;
}

export function useRenameHandler({ doctype, docName, isNew, metaSchema }: UseRenameHandlerParams) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [renameState, setRenameState] = useState<RenameDialogState | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  /**
   * Check if the save should be intercepted for a rename.
   * Returns true if rename dialog was shown (caller should abort normal save).
   */
  const checkForRename = useCallback(
    (formData: Record<string, any>): boolean => {
      if (isNew || !docName || !metaSchema) return false;

      const detection = detectRename(metaSchema, formData, docName);
      if (!detection) return false;

      setRenameState({
        oldName: detection.oldName,
        newName: detection.newName,
        autoNameField: detection.autoNameField,
      });
      return true;
    },
    [isNew, docName, metaSchema]
  );

  /**
   * Execute the rename after user confirms the dialog.
   * If formData and originalData are provided, also saves other field changes
   * after the rename succeeds (two-step save).
   */
  const handleRenameConfirm = useCallback(async (
    formData?: Record<string, any>,
    originalData?: Record<string, any>,
  ) => {
    if (!renameState) return;

    const { oldName, newName, autoNameField } = renameState;
    setRenameState(null);
    setIsRenaming(true);

    try {
      const erp = getERPClient();
      const actualNewName = await erp.rename({
        doctype,
        oldName,
        newName: newName.trim(),
      });

      // Step 2: Save other field changes against the new name
      if (formData && originalData) {
        const otherChanges = computeNonRenameChanges(formData, originalData, autoNameField);
        if (Object.keys(otherChanges).length > 0) {
          try {
            await erp.update({ doctype, name: actualNewName, data: otherChanges });
          } catch {
            toast.warning("Renamed successfully, but failed to save other changes");
          }
        }
      }

      // Invalidate caches — both old and new name, plus list queries
      queryClient.invalidateQueries({ queryKey: ["get", doctype, oldName] });
      queryClient.invalidateQueries({ queryKey: ["get", doctype, actualNewName] });
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
      queryClient.invalidateQueries({ queryKey: ["doctype-list", doctype] });

      toast.success("Renamed successfully");
      navigate(buildDetailRoute(doctype, actualNewName), { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to rename";

      if (message.includes("already exists")) {
        toast.error(`A ${doctype} with this name already exists`);
      } else if (message.includes("permission")) {
        toast.error(`You do not have permission to rename this ${doctype}`);
      } else {
        toast.error(`Failed to rename: ${message}`);
      }
    } finally {
      setIsRenaming(false);
    }
  }, [renameState, doctype, queryClient, navigate]);

  /** Close the rename dialog without renaming. */
  const handleRenameCancel = useCallback(() => {
    setRenameState(null);
  }, []);

  return {
    /** Whether rename dialog should be shown */
    renameDialogOpen: renameState !== null,
    /** Old document name for the dialog */
    renameOldName: renameState?.oldName ?? "",
    /** New document name for the dialog */
    renameNewName: renameState?.newName ?? "",
    /** Whether a rename API call is in progress */
    isRenaming,
    /** Check formData for rename; returns true if dialog was shown */
    checkForRename,
    /** Confirm handler for the dialog */
    handleRenameConfirm,
    /** Cancel handler for the dialog */
    handleRenameCancel,
  };
}
